<template>
  <div>
    <div class="tab__headerContainer">
      <!-- ランキングセレクタ -->
      <div class="tab__rankingSelectorContainer">
        <select v-model="currentRanking" @change="reload()">
          <option v-for="(data, i) in rankings" :key="i" :value="data">
            {{ getLabel(data) }}
          </option>
        </select>
      </div>
      <!-- タブ -->
      <div class="tab__itemContainer">
        <ranking-tab-view
          v-for="tabItem in getTabItems(currentRanking.rankingType)"
          :key="tabItem.type"
          :title="title(tabItem.text)"
          :selected="shouldShow(tabItem.type)"
          :type="tabItem.type"
          @selected="select"
        ></ranking-tab-view>
      </div>
    </div>

    <!-- コンテンツ -->
    <div class="tab__contentContainer">
      <!-- 自分のスコア -->
      <my-rank-view
        v-if="Object.keys(myResult).length > 0"
        :result="this.myResult"
      ></my-rank-view>
      <!-- ランキングテーブル -->
      <ranking-table
        :results="results"
        @scrolledToBottom="fetchEntries()"
      ></ranking-table>
    </div>
  </div>
</template>

<script>
import APIRequest from '@/utils/APIRequest'
import MyRankView from '@/components/Games/MyRankView.vue'
import RankingTable from '@/components/Games/RankingTable.vue'
import RankingTabView from '@/components/Games/RankingTabView.vue'
import { getToken } from '@/utils/token'
import i18n from '@/i18n'

export const RankingTypes = {
  Overall: 1,
  Limited: 2,
}

export const TargetTypes = {
  All: 1,
  Mutual: 2,
}

export default {
  name: 'RankingTab',
  components: {
    MyRankView,
    RankingTable,
    RankingTabView,
  },
  props: {
    period: Object,
  },
  data: function () {
    return {
      rankings: [],
      results: [],
      next: 1,
      myResult: {},
      currentTargetType: TargetTypes.All,
      currentRanking: {},
      processing: false,
    }
  },
  async mounted() {
    this.fetchRankingList()
  },
  async updated() {},
  methods: {
    getLabel: function (option) {
      return this.getRankingTitle(option)
    },
    getTabItems: function (rankingType) {
      switch (rankingType) {
        case RankingTypes.Overall:
          if (getToken() === undefined) {
            //通常ランキング(トークンが設定されていないため, 相互フォローランキング無)
            return [
              {
                selected: true,
                text: 'gamesRankingAll',
                type: TargetTypes.All,
              },
            ]
          }
          //通常ランキング(相互フォローランキング有)
          return [
            {
              selected: true,
              text: 'gamesRankingAll',
              type: TargetTypes.All,
            },
            {
              selected: false,
              text: 'gamesRankingCommonFollow',
              type: TargetTypes.Mutual,
            },
          ]
        case RankingTypes.Limited:
          //期間限定ランキング(相互フォローランキング無)
          return [
            {
              selected: true,
              text: 'gamesRankingAll',
              type: TargetTypes.All,
            },
          ]
        default:
          return []
      }
    },
    title: function (localizeTag) {
      return this.$t(localizeTag)
    },
    shouldShow: function (targetType) {
      return targetType === this.currentTargetType
    },
    select: function (targetType) {
      this.currentTargetType = targetType
      this.reload()
    },
    reload: function () {
      //リセット
      this.results = []
      this.myResult = {}
      this.next = 1

      this.fetchEntries()
      this.fetchEntryWithVliveId()
    },
    fetchRankingList: async function () {
      if (this.period === undefined) {
        return
      }
      const request = new APIRequest(
        this.$config.appGatewayServerUrl,
        '/game/ranking/list',
        {
          gameId: this.period.gameId,
        },
        {
          Authorization: `Bearer ${getToken()}`,
        }
      )
      const response = await fetch(request.url, {
        method: 'POST',
        headers: request.headers,
        body: JSON.stringify(request.data),
      })

      if (!response.ok) {
        return
      }
      const payload = await response.json().then((json) => json.payload)
      this.rankings = payload.rankings

      if (this.rankings.length > 0) {
        //現在のランキング(currentRanking)の初期値はサーバから返されたリストの先頭値
        this.currentRanking = this.rankings[0]
        this.fetchEntryWithVliveId()
        this.fetchEntries()
      }
    },
    getRankingPeriodString: function (ranking) {
      const start = this.getDateParts(ranking.rankingStartTime)
      const end = this.getDateParts(ranking.rankingEndTime)
      return i18n.t('gamesRankingDurationFormat', {
        year1: start[0],
        month1: start[1],
        day1: start[2],
        year2: end[0],
        month2: end[1],
        day2: end[2],
      })
    },
    getDateParts: function (t) {
      const date = new Date(t)
      return [date.getFullYear(), date.getMonth() + 1, date.getDate()]
    },
    getRankingTitle: function (ranking) {
      switch (ranking.rankingType) {
        case RankingTypes.Overall:
          var title = ''
          if (ranking.previous) {
            title = i18n.t('gamesPreviousMonthlyRankingTitle')
          } else {
            title = i18n.t('gamesCurrentMonthlyRankingTitle')
          }
          return title + ' ' + this.getRankingPeriodString(ranking)
        case RankingTypes.Limited:
          return (
            i18n.t('gamesLimitedPeriodRankingTitle') +
            ' ' +
            this.getRankingPeriodString(ranking)
          )
        default:
          return ''
      }
    },
    fetchEntryWithVliveId: async function () {
      if (this.period === undefined) {
        return
      }
      //tokenが空の時はリクエストしない
      if (getToken() === undefined) {
        return
      }
      const request = new APIRequest(
        this.$config.appGatewayServerUrl,
        '/game/leaderboard/entry/get',
        {
          gameId: this.period.gameId,
          periodId: this.period.periodId,
          rankingType: this.currentRanking.rankingType,
          targetType: this.currentTargetType,
          previous: this.currentRanking.previous,
          name: 'score',
        },
        {
          Authorization: `Bearer ${getToken()}`,
        }
      )
      const response = await fetch(request.url, {
        method: 'POST',
        headers: request.headers,
        body: JSON.stringify(request.data),
      })

      if (!response.ok) {
        return
      }
      const payload = await response.json().then((json) => json.payload)
      //スコアが存在するときのみmyResultにセット(自分のスコアを表示する)
      if (payload.entry.score > 0) {
        this.myResult = payload.entry
      }
    },
    fetchEntries: async function () {
      if (this.period === undefined) {
        return
      }
      if (this.processing) {
        return
      }

      if (this.next == 0) {
        //nextが0になったら終端
        return
      }
      this.processing = true
      const request = new APIRequest(
        this.$config.appGatewayServerUrl,
        '/game/leaderboard/entry/list',
        {
          gameId: this.period.gameId,
          periodId: this.period.periodId,
          rankingType: this.currentRanking.rankingType,
          targetType: this.currentTargetType,
          previous: this.currentRanking.previous,
          name: 'score',
          page: this.next,
        },
        {
          Authorization: `Bearer ${getToken()}`,
        }
      )

      const response = await fetch(request.url, {
        method: 'POST',
        headers: request.headers,
        body: JSON.stringify(request.data),
      })
      if (!response.ok) {
        this.processing = false
        return
      }
      const payload = await response.json().then((json) => json.payload)
      const entries = payload.entries
      const page = payload.pagination

      this.results = this.results.concat(entries)
      this.next = page.next
      this.processing = false
    },
  },
}
</script>

<style scoped>
.tab__rankingSelectorContainer {
  width: 100%;
  height: 68px;
  padding: 9px 8px 7px;
}
.tab__rankingSelectorContainer select {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;

  width: 100%;
  height: 100%;

  border: 1px solid #e8e8e8;
  box-sizing: border-box;
  border-radius: 6px;

  padding-left: 32px;

  font-family: Hiragino Sans;
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 21px;
  display: flex;

  text-align: center;

  background-image: url(/img/calendar.svg), url(/img/arrow_drop_down.svg);
  background-repeat: no-repeat;
  background-size: 16px 16px, 24px 24px;
  background-position: 8px center, right 8px center;
  background-color: white;
}
.tab__itemContainer {
  width: 100%;
  height: 42px;
  border-bottom: 1px solid #eeeeee;
  display: flex;
}
.tab__contentContainer {
  display: block;
  margin-top: 16px;
  margin-left: 8px;
}
</style>
